/**
 * Final Score — Ponderación D1·D2·D3 + asignación de flags
 *
 * Combina los tres componentes del scoring v2 en un ScoreResult final:
 *   - Ponderación configurable (por defecto 35/40/25)
 *   - Asignación automática de flags según umbrales
 *   - getNivel() ya definido en types/scoring.ts
 *
 * @module services/scoring/finalScore
 * @version 1.1.0
 */

import type {
  D1OrigenResult,
  D2PrecisionResult,
  D3ValidacionResult,
  ScoreFlag,
  ScoreResult,
} from '../../types/scoring';
import { DEFAULT_WEIGHTS, getNivel } from '../../types/scoring';
import type { InfrastructureType } from '../../lib/InfrastructureClassifier';
import { validateSupramunicipal } from '../validation/MunicipalBoundaryValidator';

// ============================================================================
// COUBICACIÓN LEGÍTIMA
// ============================================================================

/**
 * Coordenada de una infraestructura ya procesada en el mismo municipio.
 * Se usa para detectar coubicación legítima (mismo edificio).
 */
export interface SiblingCoordinate {
  x: number;
  y: number;
  /** Nombre descriptivo de la infraestructura coubicada */
  nombre: string;
}

/**
 * Detecta si la coordenada (x, y) está coubicada con alguna infraestructura
 * hermana del mismo municipio (radio ≤ 5 m → mismo edificio).
 *
 * La coubicación es un hecho operativo legítimo, no un error de datos.
 * Ejemplos reales: GUADALINFO + CONSULTORIO MÉDICO en el mismo local,
 * SALÓN USOS MÚLTIPLES + CONSULTORIO EXFILIANA en la misma planta.
 *
 * @param x Coordenada X UTM30 de la infraestructura a evaluar
 * @param y Coordenada Y UTM30 de la infraestructura a evaluar
 * @param siblings Lista de infraestructuras ya procesadas del mismo municipio
 * @returns Nombre del co-ocupante si existe dentro de 5 m, null si no
 */
export function detectCoubicacion(
  x: number,
  y: number,
  siblings: SiblingCoordinate[]
): string | null {
  const RADIUS_M = 5;
  for (const sibling of siblings) {
    const dx = x - sibling.x;
    const dy = y - sibling.y;
    if (Math.sqrt(dx * dx + dy * dy) <= RADIUS_M) {
      return sibling.nombre;
    }
  }
  return null;
}

// ============================================================================
// CONFIGURACIÓN DE FLAGS
// ============================================================================

/**
 * Umbral de score total por debajo del cual se marca BAJA_CONFIANZA.
 */
const LOW_CONFIDENCE_THRESHOLD = 60;

/**
 * Score máximo permitido cuando ninguna fuente externa confirma la coordenada.
 * Sin validación cruzada, el sistema no puede garantizar que la ubicación
 * sea correcta aunque D1 y D2 sean altos (caso real: Taller Íllar, error 145 m, score 95).
 */
const MAX_SCORE_WITHOUT_CROSS_VALIDATION = 65;

/**
 * Umbral de resolución D2 para considerar centroide.
 */
const CENTROID_RESOLUTION = 'municipality';

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Entrada para computeFinalScore.
 */
export interface FinalScoreInput {
  d1: D1OrigenResult;
  d2: D2PrecisionResult;
  d3: D3ValidacionResult;

  /** Pesos personalizados (opcional, usa DEFAULT_WEIGHTS si omitido) */
  weights?: { d1: number; d2: number; d3: number };

  /** ¿El registro tenía coordenadas originales en el documento? */
  hasOriginalCoords: boolean;

  /** ¿El resultado proviene de geocodificación (no coords documento)? */
  isGeocoded: boolean;

  // ── Campos opcionales para supramunicipal + coubicación ──────────────────

  /**
   * ¿La coordenada quedó fuera del BBox/polígono municipal?
   * Si true, se invoca validateSupramunicipal() en lugar de aplicar
   * directamente el flag FUERA_MUNICIPIO.
   */
  outOfBounds?: boolean;

  /** Coordenada X UTM30 (necesaria para validateSupramunicipal) */
  x?: number;

  /** Coordenada Y UTM30 (necesaria para validateSupramunicipal) */
  y?: number;

  /** Código INE del municipio (necesario para validateSupramunicipal) */
  codigoINE?: string;

  /** Tipología de la infraestructura (necesaria para radio supramunicipal) */
  infrastructureType?: InfrastructureType;

  /**
   * Infraestructuras ya procesadas del mismo municipio.
   * Se usa para detectar coubicación legítima (≤ 5 m de distancia).
   */
  siblingCoordinates?: SiblingCoordinate[];
}

/**
 * Calcula el ScoreResult final combinando D1, D2 y D3.
 *
 * Algoritmo:
 * 1. Ponderación: total = d1·w1 + d2·w2 + d3·w3
 * 2. Redondeo a entero
 * 3. Nivel via getNivel()
 * 4. Flags automáticos según umbrales
 *
 * @example
 * ```ts
 * const result = computeFinalScore({
 *   d1: computeD1Origen(...),
 *   d2: computeD2Precision(...),
 *   d3: computeD3Validacion(...),
 *   hasOriginalCoords: true,
 *   isGeocoded: false,
 * });
 * // result.total → 87, result.nivel → 'ALTA'
 * ```
 */
export function computeFinalScore(input: FinalScoreInput): ScoreResult {
  const {
    d1, d2, d3,
    hasOriginalCoords, isGeocoded,
    outOfBounds, x, y, codigoINE, infrastructureType,
    siblingCoordinates,
  } = input;
  const weights = input.weights ?? DEFAULT_WEIGHTS;

  // 1. Ponderación
  const rawTotal = d1.score * weights.d1 + d2.score * weights.d2 + d3.score * weights.d3;

  // 2. Redondeo + clamp estándar [0, 100]
  let total = Math.max(0, Math.min(100, Math.round(rawTotal)));

  // 3. Cap por ausencia de validación cruzada externa.
  //    Sin ninguna fuente que confirme la coordenada, el score no puede
  //    superar MAX_SCORE_WITHOUT_CROSS_VALIDATION aunque D1 y D2 sean altos.
  const capApplied = d3.sourcesConfirming === 0 && total > MAX_SCORE_WITHOUT_CROSS_VALIDATION;
  if (capApplied) {
    total = MAX_SCORE_WITHOUT_CROSS_VALIDATION;
  }

  // 4. Supramunicipal: si la coord está fuera del polígono municipal,
  //    consultar validateSupramunicipal() antes de penalizar.
  let supramunicipalScore: number | undefined;
  let isSupramunicipalConfirmed = false;
  if (outOfBounds && x !== undefined && y !== undefined && codigoINE && infrastructureType) {
    const supraResult = validateSupramunicipal(x, y, codigoINE, infrastructureType);
    if (supraResult.reason === 'supramunicipal_valid') {
      isSupramunicipalConfirmed = true;
      supramunicipalScore = supraResult.score;
      // Limitar total al score supramunicipal (degradado 70-95 según distancia)
      total = Math.min(total, supramunicipalScore);
    } else {
      // Fuera del radio tipológico: limitar al cap estándar
      total = Math.min(total, MAX_SCORE_WITHOUT_CROSS_VALIDATION);
    }
  }

  // 5. Nivel
  const nivel = getNivel(total);

  // 6. Flags
  const flags = computeFlags({
    d1, d2, d3, total, hasOriginalCoords, isGeocoded, capApplied,
    outOfBounds: outOfBounds ?? false,
    isSupramunicipalConfirmed,
    siblingCoordinates: siblingCoordinates ?? [],
    x: x ?? 0,
    y: y ?? 0,
  });

  return {
    total,
    nivel,
    d1,
    d2,
    d3,
    flags,
    weights: { d1: weights.d1, d2: weights.d2, d3: weights.d3 },
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// FLAGS
// ============================================================================

interface FlagContext {
  d1: D1OrigenResult;
  d2: D2PrecisionResult;
  d3: D3ValidacionResult;
  total: number;
  hasOriginalCoords: boolean;
  isGeocoded: boolean;
  /** Indica que el cap MAX_SCORE_WITHOUT_CROSS_VALIDATION fue aplicado */
  capApplied: boolean;
  /** ¿La coord quedó fuera del polígono municipal? */
  outOfBounds: boolean;
  /** ¿validateSupramunicipal confirmó que es legítimamente supramunicipal? */
  isSupramunicipalConfirmed: boolean;
  /** Lista de coords hermanas para detección de coubicación */
  siblingCoordinates: SiblingCoordinate[];
  /** Coord X (para detectCoubicacion) */
  x: number;
  /** Coord Y (para detectCoubicacion) */
  y: number;
}

/**
 * Determina los flags aplicables al resultado.
 *
 * Reglas (en orden de prioridad):
 * - SIN_COORDENADAS: total = 0 y sin coords originales
 * - CENTROID_FALLBACK: resolución = municipality
 * - SIN_VALIDACION_EXTERNA: ninguna fuente externa confirmó (cap 65 aplicado)
 * - BAJA_CONFIANZA: total < 60
 * - GEOCODIFICADO: sin coords originales, resultado geocodificado
 * - CROSS_VALIDATED: D3 crossValidated = true
 * - VALIDADO: total ≥ 75 y D3 crossValidated
 * - USAR_REFERENCIA: D2 documentBonus = 0 y D1 score ≥ 70
 * - REVISION_MANUAL: total 40-59
 * - REVISION_MENOR: total 60-74 sin cross-validation
 * - FUENTE_OFFLINE: D1 offlineBonus > 0
 */
export function computeFlags(ctx: FlagContext): ScoreFlag[] {
  const flags: ScoreFlag[] = [];
  const {
    d1, d2, d3, total,
    hasOriginalCoords, isGeocoded, capApplied,
    outOfBounds, isSupramunicipalConfirmed,
    siblingCoordinates, x, y,
  } = ctx;

  // Sin coordenadas: caso extremo
  if (total === 0 && !hasOriginalCoords) {
    flags.push('SIN_COORDENADAS');
    return flags; // No tiene sentido añadir más flags
  }

  // Centroide municipal como fallback
  if (d2.resolution === CENTROID_RESOLUTION) {
    flags.push('CENTROID_FALLBACK');
  }

  // Cap por ausencia de validación externa (debe preceder a BAJA_CONFIANZA para distinguirlo)
  if (capApplied) {
    flags.push('SIN_VALIDACION_EXTERNA');
  }

  // ── Supramunicipal / fuera de municipio ──────────────────────────────────
  if (outOfBounds) {
    if (isSupramunicipalConfirmed) {
      // Infra legítimamente fuera del polígono municipal (bomberos, EDAR, etc.)
      flags.push('SUPRAMUNICIPAL_CONFIRMADO');
    } else {
      // Error real: fuera del radio tipológico
      flags.push('FUERA_MUNICIPIO');
    }
  }

  // ── Coubicación legítima ─────────────────────────────────────────────────
  if (siblingCoordinates.length > 0) {
    const coubicado = detectCoubicacion(x, y, siblingCoordinates);
    if (coubicado !== null) {
      flags.push('COMPARTE_UBICACION');
      // Sin penalización: la coubicación es un hecho operativo, no un error
    }
  }

  // Baja confianza
  if (total < LOW_CONFIDENCE_THRESHOLD) {
    flags.push('BAJA_CONFIANZA');
  }

  // Geocodificado (sin coords originales)
  if (!hasOriginalCoords && isGeocoded) {
    flags.push('GEOCODIFICADO');
  }

  // Cross-validated
  if (d3.crossValidated) {
    flags.push('CROSS_VALIDATED');
  }

  // Validado: alta confianza + cross-validation
  if (total >= 75 && d3.crossValidated) {
    flags.push('VALIDADO');
  }

  // Usar referencia: sin coords documento, pero fuente fiable
  if (d2.documentBonus === 0 && d1.score >= 70 && !hasOriginalCoords) {
    flags.push('USAR_REFERENCIA');
  }

  // Revisión manual: zona baja
  if (total >= 40 && total < 60) {
    flags.push('REVISION_MANUAL');
  }

  // Revisión menor: zona media sin cross
  if (total >= 60 && total < 75 && !d3.crossValidated) {
    flags.push('REVISION_MENOR');
  }

  // Fuente offline
  if (d1.offlineBonus > 0) {
    flags.push('FUENTE_OFFLINE');
  }

  return flags;
}
